import { create } from 'zustand'
import { productFavoriteService } from '@/services/productFavoriteService'

type ProductFavoritesState = {
  shopId: number | null
  productIds: Set<number>
  loading: boolean
  loaded: boolean
  pendingIds: Set<number>
  ensureLoaded: (shopId: number) => Promise<void>
  isFavorite: (productId: number) => boolean
  isPending: (productId: number) => boolean
  toggle: (shopId: number, productId: number) => Promise<{ ok: boolean; favorited?: boolean; message?: string }>
  clear: () => void
}

export const useProductFavoritesStore = create<ProductFavoritesState>((set, get) => ({
  shopId: null,
  productIds: new Set(),
  loading: false,
  loaded: false,
  pendingIds: new Set(),

  ensureLoaded: async (shopId: number) => {
    const state = get()
    if (state.loading) return
    if (state.loaded && state.shopId === shopId) return

    set({ loading: true, shopId })
    try {
      const res = await productFavoriteService.getMyFavoriteIds()
      const ids = res.success && res.data?.product_ids ? res.data.product_ids : []
      set({
        productIds: new Set(ids.map(Number).filter((id) => Number.isInteger(id) && id > 0)),
        loaded: true,
        loading: false,
        shopId,
      })
    } catch {
      set({ productIds: new Set(), loaded: true, loading: false, shopId })
    }
  },

  isFavorite: (productId: number) => get().productIds.has(productId),

  isPending: (productId: number) => get().pendingIds.has(productId),

  toggle: async (shopId: number, productId: number) => {
    await get().ensureLoaded(shopId)
    const currentlyFavorite = get().productIds.has(productId)
    const nextPending = new Set(get().pendingIds)
    nextPending.add(productId)
    set({ pendingIds: nextPending })

    const optimistic = new Set(get().productIds)
    if (currentlyFavorite) optimistic.delete(productId)
    else optimistic.add(productId)
    set({ productIds: optimistic })

    try {
      if (currentlyFavorite) {
        await productFavoriteService.removeFavorite(productId)
      } else {
        await productFavoriteService.addFavorite(productId)
      }
      return { ok: true, favorited: !currentlyFavorite }
    } catch (e: unknown) {
      const rollback = new Set(get().productIds)
      if (currentlyFavorite) rollback.add(productId)
      else rollback.delete(productId)
      set({ productIds: rollback })
      const err = e as { response?: { data?: { message?: string } }; message?: string }
      return {
        ok: false,
        message: err.response?.data?.message || err.message || 'Failed to update favorite',
      }
    } finally {
      const cleared = new Set(get().pendingIds)
      cleared.delete(productId)
      set({ pendingIds: cleared })
    }
  },

  clear: () =>
    set({
      shopId: null,
      productIds: new Set(),
      loading: false,
      loaded: false,
      pendingIds: new Set(),
    }),
}))
